import React, { Suspense, lazy, useEffect, useMemo } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { getAccessToken } from '@/utils/authUtils'
import { generateRoutes, type RouteConfig } from '@/router'
import { PUBLIC_PATHS } from '@/constants/routerConstants'
import Loading from '@/components/layout/Loading'
import BasicLayout from '@/components/layout/BasicLayout'
import SessionExpiredLoginModal from '@/components/login/SessionExpiredLoginModal'
import { useMenuChucNang } from '@/hooks/useMenuChucNang'
import { useSsoMenu } from '@/contexts/SsoMenuContext'
import { applyAppFavicon } from '@/theme/ThemeFaviconUpdater'

const UnauthorizedPage = lazy(() => import('@/components/404/Unauthorized'))
const NotFoundPage = lazy(() => import('@/components/404/NotFound'))
const PermissionLoadErrorPage = lazy(() => import('@/components/404/PermissionLoadError'))

/**
 * Có token (kể cả đã hết hạn). Không có token / API trả 401 → `SessionExpiredLoginModal`
 * hiện form đăng nhập tại chỗ, không chuyển sang `/login`.
 */
const hasSession = (): boolean => !!getAccessToken()

/**
 * `generateRoutes` tạo `lazy(...)` mới mỗi lần gọi → component khác identity → React unmount/mount
 * lại cả trang (gọi lại toàn bộ API). Giữ component theo `name` route để quyền đổi không remount.
 */
const routeComponentCache = new Map<string, RouteConfig['component']>()
const stabilizeRouteComponents = (list: RouteConfig[]): RouteConfig[] =>
  list.map((r) => {
    const cached = routeComponentCache.get(r.name)
    if (cached) return cached === r.component ? r : { ...r, component: cached }
    routeComponentCache.set(r.name, r.component)
    return r
  })

const RenderRoute: React.FC<{
  config: RouteConfig
}> = ({ config }) => {
  const { layout: Layout, component: Component } = config
  const needAuth = !PUBLIC_PATHS.includes(config.path)

  if (needAuth && !hasSession()) {
    return <Loading />
  }

  return (
    <Layout>
      <Suspense fallback={<Loading />}>
        <Component />
      </Suspense>
    </Layout>
  )
}

const AppRoutes: React.FC = () => {
  const { status } = useSsoMenu()
  const { webChucNangAll } = useMenuChucNang()
  const chucNangKey = webChucNangAll.join('\u0001')
  const routes = useMemo<RouteConfig[]>(
    () => stabilizeRouteComponents(generateRoutes(webChucNangAll)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chucNangKey],
  )
  const allRoutes = useMemo<RouteConfig[]>(
    () => stabilizeRouteComponents(generateRoutes([], { grantAll: true })),
    [],
  )
  const forbiddenRoutes = useMemo<RouteConfig[]>(() => {
    const allowedPaths = new Set(routes.map((r) => r.path))
    return allRoutes
      .filter((r) => !allowedPaths.has(r.path))
      .map((r) => ({
        ...r,
        name: `Forbidden_${r.name}`,
        component: UnauthorizedPage,
        title: 'Không có quyền',
      }))
  }, [routes, allRoutes])
  const location = useLocation()
  const isPublicPath = PUBLIC_PATHS.includes(location.pathname)
  const waitingMenu =
    hasSession() &&
    !isPublicPath &&
    (status === 'idle' || status === 'loading' || status === 'unauthorized')
  const menuLoadFailed = hasSession() && !isPublicPath && status === 'error'

  useEffect(() => {
    if (waitingMenu) return
    if (!isPublicPath && !hasSession()) {
      document.title = 'BH - Đăng nhập'
      applyAppFavicon('bh')
      return
    }
    if (menuLoadFailed) {
      document.title = 'BH - Không tải được quyền'
      applyAppFavicon('bh')
      return
    }
    const current = [...routes, ...forbiddenRoutes].find((r) => {
      if (r.path.includes(':')) {
        const pattern = r.path.replace(/:[^/]+/g, '[^/]+')
        return new RegExp('^' + pattern + '$').test(location.pathname)
      }
      return location.pathname === r.path
    })
    const laKho = current?.module === 'kho'
    document.title = `${laKho ? 'KHO' : 'BH'} - ${current?.title ?? 'Trang không tồn tại'}`
    applyAppFavicon(laKho ? 'kho' : 'bh')
  }, [location.pathname, routes, forbiddenRoutes, menuLoadFailed, waitingMenu, isPublicPath])

  if (waitingMenu) {
    return <Loading />
  }

  if (menuLoadFailed) {
    return (
      <BasicLayout>
        <Suspense fallback={<Loading />}>
          <PermissionLoadErrorPage />
        </Suspense>
      </BasicLayout>
    )
  }

  return (
    <Routes>
      {routes.map((config) => (
        <Route key={config.name} path={config.path} element={<RenderRoute config={config} />} />
      ))}
      {forbiddenRoutes.map((config) => (
        <Route key={config.name} path={config.path} element={<RenderRoute config={config} />} />
      ))}
      <Route
        path="*"
        element={
          <BasicLayout>
            <Suspense fallback={<Loading />}>
              <NotFoundPage />
            </Suspense>
          </BasicLayout>
        }
      />
    </Routes>
  )
}

const AppRouter: React.FC = () => (
  <>
    <AppRoutes />
    <SessionExpiredLoginModal />
  </>
)

export default AppRouter
